//@ts-check
import React, { memo, Suspense, useCallback, useState } from 'react';
import {
  deletePaymentMutation,
  updatePaymentMethod
} from '../../api/payment.api';
import { useApiFetch } from '../../hooks/useApiFetch';
import GeneralModal from '../modal';
import { Carousel, CarouselContent } from '../ui/carousel';

import { ayeApi } from '../../core';
import CardMethod from '../../pages/home/components/CardMethod';
import DuitnowMethod from '../../pages/home/components/DuitnowMethod';
import EWalletMethod from '../../pages/home/components/EWalletMethod';

const PaymentMethods = memo(() => {
  const { data, isLoading, setData, refetch } = useApiFetch(
    () => {
      return ayeApi.getPaymentMethods({});
    },
    undefined,
    {
      cacheKey: 'payment_consents'
    }
  );
  const [showRemove, setShowRemove] = useState(false);
  const [selectedCard, setSelectedCard] = useState('');
  const [modal, setModal] = useState(false);

  const handleTerminate = () => {
    setShowRemove(!showRemove);
    // Add your close logic here
  };

  const handleModalClose = useCallback(() => {
    setModal(false);
  }, []);

  /**
   * @param {string} id
   * */
  const handleOpen = useCallback((id = '') => {
    setModal(true);
    setSelectedCard(id);
  }, []);

  /**
   * @param {string} id
   * @param  {boolean} checked
   */
  const handlePreferredMethod = useCallback(
    async (id = '', checked = true) => {
      console.debug(
        'ℹ️ ~ file: payment-card.js:204 ~ handlePreferredMethod ~ id:',
        id
      );
      const prevData = data;

      if (data?.payment_consents) {
        const updatedConsents = data.payment_consents?.map((consent) => ({
          ...consent,
          preferred: consent.payment_consent_id === id
        }));
        setData({ payment_consents: [...updatedConsents] });
      }
      await updatePaymentMethod(id)
        .then(() => {
          console.info('Methods updated.');
          // update the methods optimisticly
        })
        .catch((e) => {
          console.error(e);
          if (prevData) {
            setData({ payment_consents: [...prevData.payment_consents] });
          }
        });
    },
    [data]
  );

  // TODO - Show confirmation dialog before deleting
  const handleDelete = async (id?: string) => {
    const prevData = data;
    if (data?.payment_consents) {
      const updatedConsents = data.payment_consents.filter(
        (consent) => consent.payment_consent_id !== id
      );
      setData({ payment_consents: [...updatedConsents] });
    }
    await deletePaymentMutation(id)
      .then(() => {
        console.info('Methods updated.');
        // update the methods optimisticly
      })
      .catch((e) => {
        console.error(e);
        if (prevData) {
          setData({ payment_consents: [...prevData.payment_consents] });
        }
      });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="mb-6 h-6 w-52 rounded bg-gray-200"></div>
          <div className="mb-6 h-8 w-48 rounded bg-gray-200"></div>
          <div className="flex flex-row flex-nowrap space-x-4 overflow-x-auto">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-56 min-w-[20rem] basis-[calc(80%_-_0.5rem)] rounded-xl bg-gray-200 sm:basis-[50%] md:basis-1/3 lg:basis-1/5"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // const handleDeletePaymentMethod = async (id: string) => {
  //   console.log('Deleting payment method with ID:', id)
  // }

  // TODO: refactor this to use a single component for all payment methods
  return (
    <div className="">
      <Carousel
        opts={{
          align: 'start'
        }}
        className="z-20 max-w-full bg-inherit"
      >
        <CarouselContent className={'items-stretch gap-6 px-4 sm:px-6 lg:px-8'}>
          {data?.payment_consents?.map((method) => {
            return (
              <React.Fragment key={method.payment_consent_id}>
                {/* <div
                  className='bg-gradient-to-r rounded-3xl px-6 pt-4 pb-1 text-white shadow-lg sm:mx-auto'
                  style={{
                    background: `linear-gradient(-55.329669692156095deg, rgba(248, 99, 64, 1) 0.000005960464117517737%, rgba(254, 199, 69, 1) 100.00000000000234%)`,
                  }}
                >
                  <div className='flex justify-between items-center'>
                    <h6 className='text-[0.625rem]'>Your Preferred Payment Method</h6>
                    <div className='flex items-center space-x-2'>
                      <button
                        type='button'
                        onClick={() => handleDeletePaymentMethod(method?.payment_consent_id)}
                        aria-label='Delete'
                      >
                        <TrashIcon
                          className='h-6 w-6 text-white'
                          aria-hidden='true'
                        />
                      </button>
                    </div>
                  </div>
                </div> */}
                {(() => {
                  const normalizedMethod = method.method.toLowerCase();
                  const handlePaymentMethodsModified = () => {
                    refetch();
                  }; // Define this function or import it if it's defined elsewhere
                  switch (normalizedMethod) {
                    case 'duitnow_consent':
                    case 'duitnow_id':
                      return (
                        <DuitnowMethod
                          key={method.payment_consent_id}
                          method={method}
                          handlePaymentMethodsModified={
                            handlePaymentMethodsModified
                          }
                        />
                      );
                    // ? rpp_duitnowqr
                    case 'tng-ewallet':
                    case 'tng-grabpay':
                    case 'shopeepay':
                    case 'tng-truemoney':
                    case 'tng-gcash':
                    case 'tng-wechatpay':
                    case 'alipayplus_dana':
                    case 'grabpay':
                      return (
                        <EWalletMethod
                          key={method.payment_consent_id}
                          method={method}
                          handlePaymentMethodsModified={
                            handlePaymentMethodsModified
                          }
                        />
                      );
                    case 'card': // we use card for both debit and credit
                    case 'debit':
                    case 'credit':
                      return (
                        <CardMethod
                          key={method.payment_consent_id}
                          method={method}
                          handlePaymentMethodsModified={
                            handlePaymentMethodsModified
                          }
                        />
                      );
                    default:
                      return null;
                  }
                })()}
              </React.Fragment>
            );
          })}
        </CarouselContent>
      </Carousel>
      <Suspense>
        <GeneralModal
          consentRef={selectedCard}
          open={modal}
          setOpen={setModal}
          onClose={handleModalClose}
          // onAvatarSelect={ handleAvatarSelect }
        />
      </Suspense>
    </div>
  );
});

export { PaymentMethods };
